import { QueryKey, useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { ServerError } from "./types";

import { buildString, getQueryKey } from "@/helpers";

type QueryDataItem = {
  id: number;
  [key: string]: unknown;
};

export type DeleteVariables = { id?: number | string };
export type DeleteContext = { queryData: QueryDataItem[] | undefined } | void;

export type UseDeleteOptions<
  TData = unknown,
  TError = AxiosError<ServerError>,
  TVariables = DeleteVariables,
  TContext = DeleteContext
> = Partial<Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "mutationFn">>;

interface UseDeleteProps<TData> {
  queryKey?: QueryKey;
  endpoint: string;
  options?: UseDeleteOptions<TData>;
}

export function useDelete<TData>({
  endpoint,
  queryKey = getQueryKey({ endpoint }),
  options = {},
}: UseDeleteProps<TData>) {
  const queryClient = useQueryClient();

  return useMutation<TData, AxiosError<ServerError>, DeleteVariables, DeleteContext>({
    ...options,
    mutationFn: async ({ id }) => (await axios.delete(buildString([endpoint, id], { separator: "/" }))).data,
    onMutate: (variables) => {
      if (options?.onMutate) {
        options.onMutate(variables);
      }

      const queryData = queryClient.getQueryData<QueryDataItem[]>(queryKey);

      if (Array.isArray(queryData)) {
        queryClient.setQueryData<QueryDataItem[]>(
          queryKey,
          queryData.filter((item) => item.id !== variables.id)
        );
      }

      return {
        queryData,
      };
    },
    onSuccess: (data, variables, context) => {
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
      queryClient.setQueryData(queryKey, (context as { queryData: QueryDataItem[] | undefined })?.queryData);
    },
    onSettled: async (data, error, variables, context) => {
      if (options.onSettled) {
        options.onSettled(data, error, variables, context);
      }
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}
